import { Link as WaspRouterLink, routes } from "wasp/client/router";
import { Link as RouterLink } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { ChevronDown } from "lucide-react";
import { useQuery, getGlobalConfig } from "wasp/client/operations";

// 3D Building Icons
import residentialIcon from "../../client/icons/residential_m3d.png";
import academicsIcon from "../../client/icons/academics_m3d.png";
import universityIcon from "../../client/icons/university_m3d.png";
import studentLifeIcon from "../../client/icons/student_life_m3d.png";
import hospitalIcon from "../../client/icons/hospital_m3d.png";

export default function Hero() {
  const { data: config } = useQuery(getGlobalConfig);
  const isRegistrationEnabled = (config as any)?.registrationEnabled ?? true;
  const brandName = (config as any)?.brandName || "Gedoba";
  const brandSubtitle =
    (config as any)?.brandSubtitle || "Your digital building community";

  return (
    <div className="relative min-h-screen w-full overflow-hidden flex items-center justify-center">
      {/* Animated background shapes */}
      <FloatingShapes />

      {/* Main content */}
      <div className="relative z-10 mx-auto max-w-5xl px-6 lg:px-8 text-center">
        {/* Top icons row for mobile/tablet */}
        <div className="flex lg:hidden items-center justify-center gap-8 mb-12 animate-fade-in-up">
          <Static3DIcon src={residentialIcon} alt="Residences" />
          <Static3DIcon src={universityIcon} alt="Campus buildings" />
        </div>

        {/* Badge */}
        <div className="animate-fade-in-up mb-8">
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium">
            <span className="w-2 h-2 rounded-full bg-primary animate-pulse"></span>
            {brandSubtitle}
          </span>
        </div>

        {/* Main headline */}
        <h1 className="animate-fade-in-up animate-delay-100 text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.1] mb-6">
          Experience{" "}
          <span className="relative">
            <span className="text-gradient-primary animate-gradient-text bg-gradient-to-r from-primary via-secondary to-accent bg-clip-text text-transparent">
              {brandName}
            </span>
          </span>
          <br />
          <span className="text-muted-foreground">all in one place</span>
        </h1>

        {/* Subheadline - concise */}
        <p className="animate-fade-in-up animate-delay-200 mx-auto max-w-2xl text-xl sm:text-2xl text-muted-foreground mb-10 leading-relaxed">
          Notices, events, services, and community for every building you operate.
          <span className="hidden sm:inline">
            {" "}
            One portal for malls, residences, campuses, and mixed portfolios.
          </span>
        </p>

        {/* CTA buttons */}
        <div className="animate-fade-in-up animate-delay-300 flex flex-col sm:flex-row items-center justify-center gap-4">
          {isRegistrationEnabled && (
            <Button
              size="lg"
              variant="default"
              className="h-14 px-8 text-lg rounded-full shadow-lg hover:shadow-xl transition-all hover:scale-105"
              asChild
            >
              <RouterLink to={routes.SignupRoute.to}>Sign Up</RouterLink>
            </Button>
          )}
          <Button
            size="lg"
            variant="outline"
            className="h-14 px-8 text-lg rounded-full hover:scale-105 transition-all"
            asChild
          >
            <RouterLink to={routes.LoginRoute.to}>Log In</RouterLink>
          </Button>
        </div>


      </div>


      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce-slow">
        <ChevronDown className="w-8 h-8 text-muted-foreground" />
      </div>
    </div>
  );
}

function FloatingShapes() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {/* Large gradient blob - top right */}
      <div
        className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full animate-pulse-glow"
        style={{
          background:
            "radial-gradient(circle, hsl(var(--primary) / 0.15) 0%, transparent 70%)",
        }}
      />

      {/* Medium gradient blob - bottom left */}
      <div
        className="absolute -bottom-20 -left-20 w-[500px] h-[500px] rounded-full animate-pulse-glow"
        style={{
          background:
            "radial-gradient(circle, hsl(var(--secondary) / 0.12) 0%, transparent 70%)",
          animationDelay: "2s",
        }}
      />

      {/* 3D building icons with hover effect */}
      <Floating3DIcon
        src={residentialIcon}
        alt="Dormitories"
        className="hidden lg:block top-[15%] left-[10%]"
      />
      <Floating3DIcon
        src={universityIcon}
        alt="Main Campus"
        className="hidden lg:block top-[15%] right-[10%]"
      />


      {/* Grid pattern overlay */}
      <div
        className="absolute inset-0 opacity-[0.02] dark:opacity-[0.05]"
        style={{
          backgroundImage: `
            linear-gradient(hsl(var(--foreground)) 1px, transparent 1px),
            linear-gradient(90deg, hsl(var(--foreground)) 1px, transparent 1px)
          `,
          backgroundSize: "60px 60px",
        }}
      />
    </div>
  );
}

interface Floating3DIconProps {
  src: string;
  alt: string;
  className: string;
}

function Floating3DIcon({ src, alt, className }: Floating3DIconProps) {
  return (
    <div className={`absolute ${className} group`}>
      <Static3DIcon src={src} alt={alt} />
    </div>
  );
}

function Static3DIcon({ src, alt, className = "" }: Omit<Floating3DIconProps, "className"> & { className?: string }) {
  return (
    <div className={`relative transition-all duration-300 hover:scale-110 ${className}`}>
      <img
        src={`${src}?v=2`}
        alt={alt}
        className="w-16 h-16 sm:w-24 sm:h-24 lg:w-32 xl:w-40 object-contain drop-shadow-lg"
      />
    </div>
  );
}
